import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Loader2, Camera, Users, Sparkles, ChevronRight, Check } from 'lucide-react';
import { toast } from 'sonner';
import { createTeam, addPlayer, scanLineupCard } from '../lib/api';
import { useAuthStore } from '../store/authStore';

/**
 * OnboardingPage — the "Wow in 60 seconds" flow for brand-new coaches.
 *
 * 3 steps:
 *   1. Pick sport (Baseball / Softball)
 *   2. Pick age group (7-8, 9-10, 11-12, 13+, custom)
 *   3. Name the team + add roster — via AI Lineup Scan (snap a photo) OR manual quick-add
 *
 * On finish: team is created, optional first game is staged, user lands on
 * /lineup with the new team active. They've gone from signup → ready-to-score
 * in under a minute. This page is the conversion engine.
 *
 * Skip behavior: page exits to /dashboard if the user already has at least
 * one team (already onboarded). Lives at /onboarding.
 */
const SPORTS = [
  { key: 'baseball', label: 'Baseball', emoji: '⚾', color: '#0E6B36' },
  { key: 'softball', label: 'Softball', emoji: '🥎', color: '#D4A03A' },
];

const AGE_GROUPS = [
  { key: '7-8',   label: '7-8',   sublabel: 'T-ball / Coach Pitch' },
  { key: '9-10',  label: '9-10',  sublabel: 'Minors / Player Pitch' },
  { key: '11-12', label: '11-12', sublabel: 'Majors / Little League' },
  { key: '13-16', label: '13-16', sublabel: 'Travel / Pony / Babe Ruth' },
  { key: 'custom', label: 'Other', sublabel: 'College / Adult / Custom' },
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { user, teamsAsCoach, updateCoachStatus } = useAuthStore();
  const [step, setStep] = useState(1);
  const [sport, setSport] = useState(null);
  const [ageGroup, setAgeGroup] = useState(null);
  const [teamName, setTeamName] = useState('');
  const [manualPlayers, setManualPlayers] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  // Already onboarded? Bounce to the real dashboard.
  React.useEffect(() => {
    if (teamsAsCoach && teamsAsCoach.length > 0) {
      navigate('/dashboard', { replace: true });
    }
  }, [teamsAsCoach, navigate]);

  const finishWithPlayers = async (players) => {
    if (!teamName.trim()) { toast.error('Give your team a name first'); return; }
    setBusy(true);
    try {
      const team = await createTeam({
        name: teamName.trim(),
        sport_type: sport,
        age_group: ageGroup,
      });
      // Add players one at a time (matches existing API contract)
      for (const p of players) {
        if (!p.name?.trim()) continue;
        try {
          await addPlayer(team.id, {
            name: p.name.trim(),
            jersey_number: String(p.jersey_number || '').trim(),
            position: p.position || null,
            is_pitcher: !!p.is_pitcher,
          });
        } catch (e) {
          // Best-effort: a single failed player shouldn't abort the whole flow
          console.warn('player add skipped', e);
        }
      }
      // Refresh coach status so navbar/sidebar see the new team
      try { updateCoachStatus(true, [...(teamsAsCoach || []), team.id]); } catch (e) { /* non-fatal */ }
      toast.success(`Team "${team.name}" created with ${players.length} player${players.length === 1 ? '' : 's'}!`);
      // Land them right on the lineup page so they can score immediately
      navigate(`/lineup?team=${team.id}`, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create team — try again?');
    } finally {
      setBusy(false);
    }
  };

  const handleScanFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const reader = new FileReader();
      const base64 = await new Promise((res, rej) => {
        reader.onload = () => res(reader.result);
        reader.onerror = rej;
        reader.readAsDataURL(file);
      });
      const onlyB64 = base64.split(',')[1];
      const data = await scanLineupCard(onlyB64);
      if (!data.players || data.players.length === 0) {
        toast.error('No players detected — try a clearer photo or use Manual Add');
        return;
      }
      toast.success(`Scanned ${data.players.length} players!`);
      await finishWithPlayers(data.players);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed — try Manual Add');
      setBusy(false);
    }
  };

  const handleManualAdd = async () => {
    const lines = manualPlayers.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length === 0) { toast.error('Add at least one player name'); return; }
    const players = lines.map(line => {
      // Try "Name #12" or "Name 12" or just "Name"
      const m = line.match(/^(.+?)\s*#?(\d+)\s*$/);
      if (m) return { name: m[1].trim(), jersey_number: m[2] };
      return { name: line, jersey_number: '' };
    });
    await finishWithPlayers(players);
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'radial-gradient(ellipse at top, #131E36 0%, #0A1322 70%)' }}
      data-testid="onboarding-page"
    >
      <div className="max-w-xl w-full space-y-4">
        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 mb-2" data-testid="onboarding-stepper">
          {[1, 2, 3].map(n => (
            <div key={n} className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                  step > n ? 'text-stone-900' : step === n ? 'text-stone-900 scale-110' : 'text-slate-400'
                }`}
                style={{
                  background: step > n
                    ? 'linear-gradient(135deg, #2EBE6B 0%, #1FA85A 100%)'
                    : step === n
                      ? 'linear-gradient(135deg, #F5C455 0%, #D4A03A 100%)'
                      : 'rgba(255,255,255,0.06)',
                  boxShadow: step === n ? '0 0 16px rgba(245, 196, 85, 0.5)' : 'none',
                }}
              >
                {step > n ? <Check size={16} /> : n}
              </div>
              {n < 3 && <div className="w-6 h-px mx-1" style={{ background: 'rgba(212, 160, 58, 0.3)' }} />}
            </div>
          ))}
        </div>

        {/* Header */}
        <div className="text-center mb-2">
          <img
            src="/diamond-scorer-logo.jpg"
            alt="Diamond Scorer"
            width={56}
            height={56}
            className="mx-auto mb-3"
            style={{
              borderRadius: 12,
              boxShadow: '0 0 16px rgba(46,190,107,0.4), 0 2px 6px rgba(0,0,0,0.4)',
              objectFit: 'cover',
            }}
          />
          <p className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#F5C455' }}>
            Quick Setup · {step}/3
          </p>
          <h1 className="text-3xl md:text-4xl font-black mt-1 tracking-tight"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {step === 1 && <>Pick your <span style={{ color: '#2EBE6B' }}>sport</span></>}
            {step === 2 && <>Pick your <span style={{ color: '#F5C455' }}>age group</span></>}
            {step === 3 && <>Add your <span style={{ color: '#2EBE6B' }}>roster</span></>}
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            {step === 1 && 'We’ll tailor pitch counts and rules to your level.'}
            {step === 2 && 'Pitch-count rest rules auto-apply for this age band.'}
            {step === 3 && 'Snap a photo of your lineup card OR type names — your choice.'}
          </p>
        </div>

        {/* Step 1 - Sport */}
        {step === 1 && (
          <div className="grid grid-cols-2 gap-3" data-testid="onboarding-step-sport">
            {SPORTS.map(s => (
              <button
                key={s.key}
                onClick={() => { setSport(s.key); setStep(2); }}
                data-testid={`sport-${s.key}`}
                className="rounded-2xl p-6 text-center transition-all hover:scale-[1.03] active:scale-95"
                style={{
                  background: sport === s.key
                    ? `linear-gradient(135deg, ${s.color} 0%, ${s.color}AA 100%)`
                    : 'rgba(26, 37, 66, 0.6)',
                  border: `2px solid ${sport === s.key ? '#F5C455' : 'rgba(212,160,58,0.25)'}`,
                  boxShadow: sport === s.key ? '0 0 20px rgba(245,196,85,0.4)' : 'none',
                }}
              >
                <div className="text-6xl mb-2">{s.emoji}</div>
                <p className="font-extrabold text-xl text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{s.label}</p>
              </button>
            ))}
          </div>
        )}

        {/* Step 2 - Age group */}
        {step === 2 && (
          <div className="space-y-2" data-testid="onboarding-step-age">
            {AGE_GROUPS.map(a => (
              <button
                key={a.key}
                onClick={() => { setAgeGroup(a.key); setStep(3); }}
                data-testid={`age-${a.key}`}
                className="w-full rounded-xl p-4 flex items-center justify-between transition-all hover:scale-[1.01] active:scale-99"
                style={{
                  background: ageGroup === a.key
                    ? 'linear-gradient(90deg, rgba(245, 196, 85, 0.25) 0%, rgba(26, 37, 66, 0.95) 80%)'
                    : 'rgba(26, 37, 66, 0.6)',
                  border: `1px solid ${ageGroup === a.key ? '#F5C455' : 'rgba(212,160,58,0.25)'}`,
                }}
              >
                <div className="text-left">
                  <p className="font-extrabold text-lg text-white leading-none" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{a.label}</p>
                  <p className="text-slate-400 text-xs mt-1">{a.sublabel}</p>
                </div>
                <ChevronRight size={18} className="text-slate-500" />
              </button>
            ))}
            <button onClick={() => setStep(1)} className="text-slate-400 text-sm hover:text-white transition" data-testid="back-to-sport">
              ← Back
            </button>
          </div>
        )}

        {/* Step 3 - Team name + Roster (AI or manual) */}
        {step === 3 && (
          <Card className="border-0 shadow-2xl" style={{ background: '#131E36', border: '1px solid rgba(212, 160, 58, 0.3)' }}>
            <CardContent className="p-5 space-y-4" data-testid="onboarding-step-roster">
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold block mb-1.5" style={{ color: '#F5C455' }}>
                  Team Name
                </label>
                <Input
                  data-testid="onboarding-team-name"
                  placeholder="e.g. Tigers, Eagles, Bulldogs"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500"
                  autoFocus
                />
              </div>

              {/* AI Scan — the magic moment */}
              <div className="rounded-xl p-4"
                style={{ background: 'linear-gradient(135deg, rgba(46,190,107,0.15) 0%, rgba(31, 168, 90, 0.05) 100%)', border: '1px solid rgba(46,190,107,0.45)' }}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: '#2EBE6B', boxShadow: '0 0 12px rgba(46,190,107,0.5)' }}>
                    <Sparkles size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="font-extrabold text-white text-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>AI Lineup Scan</p>
                    <p className="text-slate-300 text-xs">Take a photo of any lineup card. We’ll detect names, numbers, and positions in 5 seconds.</p>
                  </div>
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => handleScanFile(e.target.files?.[0])}
                  data-testid="onboarding-scan-file"
                />
                <Button
                  data-testid="onboarding-scan-btn"
                  className="w-full font-bold text-stone-900 shadow-md h-11"
                  style={{ background: 'linear-gradient(135deg, #F5C455 0%, #D4A03A 100%)' }}
                  onClick={() => fileRef.current?.click()}
                  disabled={busy || !teamName.trim()}
                >
                  {busy ? <Loader2 className="animate-spin mr-2" size={18} /> : <Camera size={18} className="mr-2" />}
                  {busy ? 'Working...' : 'Snap Lineup Card'}
                </Button>
              </div>

              {/* OR divider */}
              <div className="flex items-center gap-3 text-xs uppercase tracking-widest" style={{ color: 'rgba(212,160,58,0.5)' }}>
                <div className="flex-1 h-px" style={{ background: 'rgba(212,160,58,0.25)' }} />
                or type
                <div className="flex-1 h-px" style={{ background: 'rgba(212,160,58,0.25)' }} />
              </div>

              {/* Manual entry */}
              <div>
                <textarea
                  data-testid="onboarding-manual-textarea"
                  placeholder="One player per line:&#10;Hank Aaron 44&#10;Mike Trout 27&#10;Aaron Judge 99"
                  value={manualPlayers}
                  onChange={(e) => setManualPlayers(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 text-white rounded-md p-3 text-sm placeholder:text-slate-500 focus:outline-none focus:border-slate-500"
                  rows={5}
                />
                <Button
                  data-testid="onboarding-manual-btn"
                  className="w-full mt-2 h-11 font-bold text-white"
                  style={{ background: '#2EBE6B' }}
                  onClick={handleManualAdd}
                  disabled={busy || !teamName.trim() || !manualPlayers.trim()}
                >
                  <Users size={18} className="mr-2" />
                  Add Players & Get Scoring
                </Button>
              </div>

              <div className="flex items-center justify-between text-xs">
                <button
                  onClick={() => setStep(2)}
                  className="text-slate-400 hover:text-white transition"
                  data-testid="back-to-age"
                >
                  ← Back
                </button>
                <button
                  onClick={async () => {
                    if (!teamName.trim()) { toast.error('Give your team a name first'); return; }
                    await finishWithPlayers([]);
                  }}
                  className="text-slate-500 hover:text-slate-300 transition"
                  data-testid="skip-roster-btn"
                >
                  Skip — I’ll add players later
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer escape hatch */}
        <p className="text-center text-slate-500 text-xs">
          You can change any of these later in Settings.
        </p>
      </div>
    </div>
  );
}
